import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CaretLeftIcon,
  PlusIcon,
  InfoIcon,
  BarbellIcon,
} from "@phosphor-icons/react";
import { profilingEndpoints, ITestingProtocol } from "../../endpoints/profiling";
import { Paginated } from "../../typings/Paginated";
import LeopartLoader from "../../shared/LeopartLoader/LeopartLoader";
import CustomButton from "../../shared/CustomButton/CustomButton";
import Typhography from "../../shared/Typhography/Typhography";
import { MetricsRow } from "./components/MetricsRow";
import { IndexHero } from "./components/IndexHero";
import { LoadVelocityChart } from "./components/LoadVelocityChart";
import { VelocityZones } from "./components/VelocityZones";
import { ProfileTrendChart } from "./components/ProfileTrendChart";
import { ProfileHistoryList } from "./components/ProfileHistoryList";
import { FVProfileChart } from "./components/FVProfileChart";
import { ConfiguraTestModal } from "../ProfilingHubPage/ConfiguraTestModal";
import styles from "./ProfilingProtocolDetailPage.module.scss";

/**
 * Mappa protocollo Profiling Hub → outputType (spec Profiling sez.3, tabella).
 * Il LAYOUT è determinato dall'outputType, non dall'argomento:
 *  - regression/fv_jump/fv_sprint → Layout A "Profilo" (curva/metriche chiave)
 *  - index → Layout B "Indice" (valore primario + zone)
 * Mirror di HUB_OUTPUT_TYPE backend (enums/profiling.ts).
 */
const HUB_OUTPUT_TYPE: Record<string, "regression" | "fv_jump" | "fv_sprint" | "index"> = {
  P1: "regression", P2: "regression", P3: "fv_jump", P3b: "fv_jump",
  P4: "fv_sprint", P5: "regression", P6: "regression", P7: "index",
  P8: "index", P9: "regression", P10: "index", P11: "index",
  P12: "index", P13: "index", P14: "index",
};
const outputTypeForHub = (hubId?: string) =>
  hubId ? HUB_OUTPUT_TYPE[hubId] : undefined;

/** Ritorna true se il profilo ha un KPI "Crea blocco" utile (sez.4 closed-loop). */
function hasUsefulKpi(outputs: Record<string, any> | undefined): boolean {
  if (!outputs) return false;
  return (
    outputs.estimated1RM != null ||
    outputs.Pmax != null ||
    outputs.F0 != null ||
    outputs.V0 != null ||
    outputs.peakRSI != null ||
    outputs.LT2 != null ||
    outputs.VIFT != null ||
    outputs.DSI != null
  );
}

interface IProfilingProtocolDetailContentProps {
  athleteId: string;
  protocolId: string;
  /** Se fornito: mostra back button che chiama la callback (uso inline) */
  onBack?: () => void;
}

export const ProfilingProtocolDetailContent: React.FC<
  IProfilingProtocolDetailContentProps
> = ({ athleteId, protocolId, onBack }) => {
  const qc = useQueryClient();

  const [blockCreated, setBlockCreated] = useState<{
    blockId: string;
    title: string;
  } | null>(null);

  /** sez.5 — profilo selezionato dalla cronologia (default: ultimo = profiles[0]) */
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

  /** sez.5 — modal Nuova profilazione */
  const [showAddModal, setShowAddModal] = useState(false);

  const generateBlockMut = useMutation({
    mutationFn: (profileId: string) =>
      profilingEndpoints.generateBlockFromProfile(profileId).then((r) => r.data),
    onSuccess: (data) => {
      setBlockCreated(data as any);
      qc.invalidateQueries({ queryKey: ["programming-blocks"] });
    },
  });

  const { data: protocolsResp } = useQuery({
    queryKey: ["profiling-protocols", "profile"],
    // `type` non è supportato come filtro server-side da listProtocols
    // (accetta solo `exerciseId`): il campo veniva già ignorato a runtime.
    queryFn: () =>
      profilingEndpoints.listProtocols().then((r) => r.data),
  });

  const { data: profiles, isLoading } = useQuery({
    queryKey: ["athlete-profiles", athleteId, protocolId],
    queryFn: () =>
      profilingEndpoints
        .listAthleteProfiles(athleteId, { protocolId, limit: 50 })
        .then((r) => r.data),
    enabled: !!athleteId && !!protocolId,
  });

  // `outputType` è restituito dal backend ma non è (ancora) presente in ITestingProtocol.
  type ProtocolWithOutputType = ITestingProtocol & {
    outputType?: "regression" | "fv_jump" | "fv_sprint" | "index";
  };

  const protocols: ProtocolWithOutputType[] =
    (protocolsResp as Paginated<ProtocolWithOutputType> | undefined)?.docs ??
    (protocolsResp as { data?: ProtocolWithOutputType[] } | undefined)?.data ??
    (Array.isArray(protocolsResp) ? protocolsResp : []) ??
    [];
  const protocol = protocols.find((p) => p._id === protocolId);

  const profileList = (profiles as any[]) ?? [];

  /** sez.5 — profilo visualizzato nel dettaglio: selezionato o ultimo */
  const activeProfile = selectedProfileId
    ? profileList.find((p) => p._id === selectedProfileId) ?? profileList[0]
    : profileList[0];

  const hubId: string | undefined = protocol?.profilingHubProtocolId;
  // Layout guidato da outputType (spec sez.3-4), non dall'hardcode VBT.
  const outputType = protocol?.outputType ?? outputTypeForHub(hubId);
  const isRegression = outputType === "regression"; // load-velocity (chart + zone)
  const profileCount = profileList.length;

  /* ── loading ─────────────────────────────────────────────────────────── */
  if (isLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <LeopartLoader size="lg" />
      </div>
    );
  }

  /* ── protocol not found ──────────────────────────────────────────────── */
  if (!protocol) {
    return (
      <div className={styles.emptyCard}>
        <InfoIcon size={32} className={styles.emptyIcon} />
        <Typhography size="bold-16" style={{ color: "#00212c", margin: 0 }}>
          Protocollo non trovato
        </Typhography>
      </div>
    );
  }

  const formattedActiveDate = activeProfile
    ? new Date(activeProfile.sessionDate).toLocaleDateString("it-IT")
    : null;

  /* sez.8 — warning R² esteso: qualsiasi profilo con outputs.R2 < 0.95 */
  const activeR2 = activeProfile?.outputs?.R2;
  const showR2Warning = activeR2 != null && activeR2 < 0.95;

  /* sez.8 — warning range velocità ridotto (Δv ≤ 0,5 m/s) */
  const velocityRange: number | undefined = activeProfile?.outputs?.velocityRange;
  const lowVelocityRange: boolean = activeProfile?.outputs?.lowVelocityRange === true;
  const showVelocityRangeWarning = lowVelocityRange;

  /* sez.4 — closed-loop "Crea blocco": non gated da isRegression */
  const canCreateBlock = activeProfile && hasUsefulKpi(activeProfile.outputs);

  return (
    <>
      {/* Back navigation */}
      {onBack ? (
        <button type="button" className={styles.backLink} onClick={onBack}>
          <CaretLeftIcon size={16} weight="bold" />
          <span>Torna a Profiling Hub</span>
        </button>
      ) : (
        <Link
          to={`/dashboard/users/${athleteId}?tab=testing&testingMode=profiling`}
          className={styles.backLink}
        >
          <CaretLeftIcon size={16} weight="bold" />
          <span>Torna a Profiling Hub</span>
        </Link>
      )}

      {/* Page header */}
      <div className={styles.pageHeader}>
        <div className={styles.titleRow}>
          <span className={styles.protocolChip}>{hubId}</span>
          <Typhography size="bold-24" style={{ color: "#00212c", margin: 0 }}>
            {protocol.name?.replace(/^P\d+\s*[—\-]\s*/, "") || protocol.name}
          </Typhography>
        </div>
        <div className={styles.subtitleRow}>
          <Typhography
            size="regular-12"
            style={{ color: "#4d4d4d", margin: 0 }}
          >
            {protocol.executionProtocol}
          </Typhography>
          <Typhography
            size="regular-12"
            style={{ color: "#b5b5b5", margin: 0 }}
          >
            {profileCount} {profileCount === 1 ? "profilo totale" : "profili totali"}
          </Typhography>
        </div>
      </div>

      {/* No profiles state */}
      {!activeProfile && (
        <div className={styles.emptyCard}>
          <BarbellIcon size={32} className={styles.emptyIcon} />
          <Typhography size="bold-16" style={{ color: "#00212c", margin: 0 }}>
            Nessun profilo registrato
          </Typhography>
          <Typhography
            size="regular-12"
            style={{ color: "#5a5a5a", margin: 0 }}
          >
            Esegui un test profilo via app coach o Configura Test.
          </Typhography>
          {/* sez.5 — pulsante Nuova profilazione anche in empty state */}
          <CustomButton
            color="primary"
            size="sm"
            iconPosition="left"
            icon={<PlusIcon size={14} weight="bold" />}
            onClick={() => setShowAddModal(true)}
          >
            Nuova profilazione
          </CustomButton>
        </div>
      )}

      {/* Main content */}
      {activeProfile && (
        <div className={styles.contentStack}>
          {/* Profilo attivo — titolo cambia se diverso dall'ultimo (sez.5) */}
          <div className={styles.card}>
            <div className={styles.sectionHeader}>
              <Typhography
                size="bold-16"
                style={{ color: "#00212c", margin: 0 }}
              >
                {selectedProfileId && selectedProfileId !== profileList[0]?._id
                  ? `Profilo del ${formattedActiveDate}`
                  : `Ultimo profilo · ${formattedActiveDate}`}
              </Typhography>
              {/* sez.4 closed-loop: non gated da isRegression */}
              {canCreateBlock && (
                <CustomButton
                  color="primary"
                  size="sm"
                  iconPosition="left"
                  icon={<PlusIcon size={14} weight="bold" />}
                  disabled={generateBlockMut.isPending}
                  onClick={() => generateBlockMut.mutate(activeProfile._id)}
                >
                  Crea blocco da profilo
                </CustomButton>
              )}
            </div>

            {blockCreated && (
              <div className={styles.successBanner}>
                <Typhography
                  size="bold-16"
                  style={{ color: "#00212c", margin: 0 }}
                >
                  Blocco creato: {blockCreated.title}
                </Typhography>
                <button
                  type="button"
                  className={styles.successBannerLink}
                  onClick={() => {
                    /* navigazione a Programmazione gestita dal parent se necessario */
                  }}
                >
                  Vai in Programmazione per utilizzarlo
                </button>
              </div>
            )}

            {/* sez.8 — warning R² esteso: tutti i profili con R² < 0.95 */}
            {showR2Warning && (
              <div className={styles.warningBanner}>
                ⚠ R² = {Number(activeR2).toFixed(3)} (&lt; 0,95):
                punto anomalo o range di carichi/velocità insufficiente — la
                stima va interpretata con cautela.
              </div>
            )}

            {/* sez.8 — warning range velocità ridotto: Δv ≤ 0,5 m/s (lowVelocityRange) */}
            {showVelocityRangeWarning && (
              <div className={styles.warningBanner}>
                ⚠ Range velocità ridotto
                {velocityRange != null
                  ? ` (Δv = ${Number(velocityRange).toFixed(2)} m/s ≤ 0,5)`
                  : " (Δv ≤ 0,5 m/s)"}
                : allarga il range di carichi per migliorare l'affidabilità
                della stima.
              </div>
            )}

            {/* sez.4.2 Layout B "Indice": IndexHero per outputType=index */}
            {outputType === "index" &&
              activeProfile.outputs &&
              Object.keys(activeProfile.outputs).length > 0 && (
                <IndexHero outputs={activeProfile.outputs} />
              )}

            {/* sez.4.1 Layout A: MetricsRow generica per profili curva/regressione */}
            {outputType !== "index" &&
              activeProfile.outputs &&
              Object.keys(activeProfile.outputs).length > 0 && (
                <MetricsRow outputs={activeProfile.outputs} />
              )}
          </div>

          {/* Load-velocity scatter — Layout A regression (VBT/sled) */}
          {isRegression && (activeProfile.inputs as any[])?.length > 0 && (
            <LoadVelocityChart
              inputs={activeProfile.inputs}
              outputs={activeProfile.outputs ?? {}}
            />
          )}

          {/* Profilo F-V — Layout A salto/sprint (curva + retta ottimale + FVimb, sez.4.1) */}
          {(outputType === "fv_jump" || outputType === "fv_sprint") &&
            activeProfile.outputs?.F0 != null && (
              <FVProfileChart outputs={activeProfile.outputs} />
            )}

          {/* Velocity/training zones — mostrate quando presenti (data-driven) */}
          {/* sez.4 MEDIA: outputType passato per titolo/unità parametrici */}
          {activeProfile.outputs?.velocityZones?.length > 0 && (
            <VelocityZones
              zones={activeProfile.outputs.velocityZones}
              outputType={outputType}
            />
          )}

          {/* Trend over time (KPI principale in cima — spec sez.5.1) */}
          {profileList.length > 1 && (
            <ProfileTrendChart profiles={profileList} hubId={hubId} />
          )}

          {/* sez.5 — cronologia cliccabile + pulsante Nuova profilazione */}
          <ProfileHistoryList
            profiles={profileList}
            outputType={outputType}
            selectedProfileId={selectedProfileId ?? profileList[0]?._id}
            onSelectProfile={setSelectedProfileId}
            onAddNew={() => setShowAddModal(true)}
          />
        </div>
      )}

      {/* sez.5 — modal Nuova profilazione (riusa ConfiguraTestModal esistente) */}
      {showAddModal && (
        <ConfiguraTestModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          athleteId={athleteId}
        />
      )}
    </>
  );
};

export default ProfilingProtocolDetailContent;
